import { useSnackbar } from 'notistack'
import { useContext, useEffect, useState } from 'react'
import { FieldErrors } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { AddEditPageContext } from 'features/inventory/page/contexts/AddEditPageContext'
import { useMutation } from 'providers/tanstack-query/useMutation'
import { mapGraphQLErrors } from 'providers/tanstack-query/utils'
import { Schema } from '../schema'
import { toDTO } from './api/mapper'
import { UPDATE_PAGE_PREBID_STACK } from './api/mutation'
import { Data, Variables } from './api/types'
import { UseUpdatePagePrebidStack } from './types'

export const useUpdatePagePrebidStack = (): UseUpdatePagePrebidStack => {
  const { t } = useTranslation('features/page', {
    keyPrefix: 'form.prebidStack.form.update',
  })
  const { enqueueSnackbar } = useSnackbar()
  const { setLoading } = useContext(AddEditPageContext)
  const [errors, setErrors] = useState<FieldErrors<Schema>>({})

  const { isPending: loading, mutateAsync } = useMutation<{ input: Variables }, Data>({
    mutation: UPDATE_PAGE_PREBID_STACK,
    onCompleted: async data => {
      const { updatePagePrebidStack } = data

      if (updatePagePrebidStack) {
        const { errors } = updatePagePrebidStack

        if (errors && errors.length > 0) {
          setErrors(mapGraphQLErrors(errors))
          enqueueSnackbar(t('error'), { variant: 'error' })
        } else {
          setErrors({})
          enqueueSnackbar(t('success'), { variant: 'success' })
        }
      }
    },
    queriesToInvalidate: ['getPage'],
  })

  useEffect(() => {
    setLoading(loading)
  }, [loading])

  const update = (data: Schema): void =>
    void mutateAsync({
      input: toDTO(data),
    })

  return { errors, update }
}
